import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  gutachtenResponseSchema,
  gutachtenVorlagenResponseSchema,
  type GutachtenRequest,
} from '@mindkeep/shared'
import { apiRequest } from '@/lib/api-client'

/**
 * Gutachten-Ausgabevorlagen (RNA M7).
 *
 * Es gibt bewusst keinen Hook, der einen Gutachtenabschnitt vom Modell
 * schreiben laesst. Der Bericht entsteht aus erfassten Fallangaben und
 * abgerufenen, freigegebenen Belegen; was nicht belegt ist, bleibt leer.
 *
 * Ein Fehlschlag mit `guardrail_violation` (HTTP 422) wird nicht geschluckt:
 * die Waechter aus M4 laufen am Upload-Chokepoint, und eine Ausgabe mit
 * unbelegten Zahlen darf nicht entstehen — auch nicht als Entwurf.
 */

export const gutachtenKeys = {
  all: ['gutachten'] as const,
  vorlagen: () => [...gutachtenKeys.all, 'vorlagen'] as const,
}

export function useGutachtenVorlagen() {
  return useQuery({
    queryKey: gutachtenKeys.vorlagen(),
    queryFn: () =>
      apiRequest({ path: '/gutachten/vorlagen', schema: gutachtenVorlagenResponseSchema }),
    staleTime: 5 * 60_000,
  })
}

export function useGutachtenErstellen() {
  return useMutation({
    mutationFn: (body: GutachtenRequest) =>
      apiRequest({ method: 'POST', path: '/gutachten', body, schema: gutachtenResponseSchema }),
    onSuccess: (data) => {
      const leer = data.abschnitte.filter((abschnitt) => abschnitt.status === 'leer').length
      toast.success(
        leer === 0
          ? 'Gutachten erstellt. Alle Abschnitte sind belegt.'
          : `Gutachten erstellt. ${leer} Abschnitt(e) bleiben leer und stehen unter «Offene Punkte».`,
      )
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Gutachten konnte nicht erstellt werden'),
  })
}
